import API_BASE from '../../utils/api';
import { formatLocalDate } from '../../utils/date';
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ProfileDropdown from "../../components/ProfileDropdown";
import AttendanceReportsTab from "../../components/AttendanceReportsTab";

const ATTENDANCE_METHOD_LABELS = {
  manual: "Manual",
  selfie_location: "Selfie + Location",
  qr_selfie: "QR + Selfie",
  cctv_facial: "CCTV Facial Recognition",
  biometric_rfid: "Biometric / RFID",
};

const PrincipalAttendance = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("class"); // 'class' | 'summary' | 'teachers'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    marked: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    percentage: 0,
  });

  // School-wide, all-classes summary
  const [classSummary, setClassSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Teacher attendance + principal verification
  const [teacherAttendance, setTeacherAttendance] = useState([]);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/classes`);
      setClasses(response.data);
      if (response.data.length > 0) {
        setSelectedClass(response.data[0]._id);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAttendance = useCallback(async () => {
    try {
      const formattedDate = formatLocalDate(selectedDate);
      const response = await axios.get(
        `${API_BASE}/attendance/class/${selectedClass}?date=${formattedDate}`,
      );
      setAttendanceData(response.data);

      // "Total Students" must come from class enrollment, not the count of
      // records marked so far — otherwise a partially-marked day understates
      // the class size and inflates the percentage.
      const totalStudents = classes.find((c) => c._id === selectedClass)?.students?.length || 0;
      const marked = response.data.length;
      const present = response.data.filter((a) => a.status === "present").length;
      const absent = response.data.filter((a) => a.status === "absent").length;
      const late = response.data.filter((a) => a.status === "late").length;
      const halfDay = response.data.filter((a) => a.status === "half-day").length;

      setStats({
        totalStudents,
        marked,
        present,
        absent,
        late,
        halfDay,
        percentage: totalStudents > 0 ? Math.round(((present + late + halfDay * 0.5) / totalStudents) * 100) : 0,
      });
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  }, [selectedClass, selectedDate, classes]);

  const fetchClassSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const formattedDate = formatLocalDate(selectedDate);
      const response = await axios.get(
        `${API_BASE}/attendance/summary?date=${formattedDate}`,
      );
      setClassSummary(response.data);
    } catch (error) {
      console.error("Error fetching attendance summary:", error);
    } finally {
      setSummaryLoading(false);
    }
  }, [selectedDate]);

  const fetchTeacherAttendance = useCallback(async () => {
    setTeacherLoading(true);
    try {
      const formattedDate = formatLocalDate(selectedDate);
      const response = await axios.get(
        `${API_BASE}/attendance/teacher/selfie-all?date=${formattedDate}`,
      );
      setTeacherAttendance(response.data);
    } catch (error) {
      console.error("Error fetching teacher attendance:", error);
    } finally {
      setTeacherLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (activeTab === "class" && selectedClass) {
      fetchAttendance();
    } else if (activeTab === "summary") {
      fetchClassSummary();
    } else if (activeTab === "teachers") {
      fetchTeacherAttendance();
    }
  }, [activeTab, selectedClass, fetchAttendance, fetchClassSummary, fetchTeacherAttendance]);

  const toggleTeacherVerification = async (teacherLoginId, verified) => {
    if (!teacherLoginId) return;
    setVerifyingId(teacherLoginId);
    try {
      await axios.put(`${API_BASE}/attendance/teacher/${teacherLoginId}/verify`, { verified });
      fetchTeacherAttendance();
    } catch (error) {
      console.error("Error updating verification:", error);
      alert(error.response?.data?.error || "Failed to update verification");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              Attendance Overview
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { key: "class", label: "By Class" },
              { key: "summary", label: "All Classes" },
              { key: "teachers", label: "Teachers" },
              { key: "reports", label: "Reports & Audit" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === tab.key
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50 border"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          {activeTab !== "reports" && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex gap-4">
              {activeTab === "class" && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Class
                  </label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {classes.map((cls) => (
                      <option key={cls._id} value={cls._id}>
                        {cls.className} - {cls.section}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  className="w-full border rounded-md px-3 py-2"
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                />
              </div>
            </div>
          </div>
          )}

          {activeTab === "reports" && <AttendanceReportsTab classes={classes} />}

          {activeTab === "class" && (
            <>
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-gray-500 text-sm">Total Students</p>
                  <p className="text-3xl font-bold mt-2">{stats.totalStudents}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-gray-500 text-sm">Present</p>
                  <p className="text-3xl font-bold mt-2 text-green-600">
                    {stats.present}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-gray-500 text-sm">Absent</p>
                  <p className="text-3xl font-bold mt-2 text-red-600">
                    {stats.absent}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-gray-500 text-sm">Late / Half Day</p>
                  <p className="text-3xl font-bold mt-2 text-yellow-600">
                    {stats.late} / {stats.halfDay}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-gray-500 text-sm">Marked</p>
                  <p className="text-3xl font-bold mt-2 text-gray-700">
                    {stats.marked}/{stats.totalStudents}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <p className="text-gray-500 text-sm">Attendance %</p>
                  <p className="text-3xl font-bold mt-2 text-indigo-600">
                    {stats.percentage}%
                  </p>
                </div>
              </div>

              {/* Attendance Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold">Attendance Details</h2>
                </div>
                <div className="p-6">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Roll No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Student Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {attendanceData.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                            No attendance marked for this class/date yet
                          </td>
                        </tr>
                      ) : (
                        attendanceData.map((record) => (
                          <tr key={record._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {record.studentId?.rollNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {record.studentId?.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  record.status === "present"
                                    ? "bg-green-100 text-green-800"
                                    : record.status === "absent"
                                      ? "bg-red-100 text-red-800"
                                      : record.status === "half-day"
                                        ? "bg-blue-100 text-blue-800"
                                        : record.status === "on-leave"
                                          ? "bg-purple-100 text-purple-800"
                                          : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {record.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === "summary" && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold">All Classes — Attendance Summary</h2>
              </div>
              <div className="p-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class Teacher</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Students</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Present</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Absent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marked</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {summaryLoading ? (
                      <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                    ) : classSummary.length === 0 ? (
                      <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No classes found</td></tr>
                    ) : (
                      classSummary.map((c) => (
                        <tr key={c.classId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">{c.className} - {c.section}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{c.classTeacher}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{c.totalStudents}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-green-700">{c.present}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-red-700">{c.absent}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{c.marked}/{c.totalStudents}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.percentage >= 75 ? "bg-green-100 text-green-800" : c.percentage >= 50 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                              {c.percentage}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "teachers" && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold">Teacher Attendance</h2>
              </div>
              <div className="p-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teacher</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checked In</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {teacherLoading ? (
                      <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                    ) : teacherAttendance.length === 0 ? (
                      <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No teachers found</td></tr>
                    ) : (
                      teacherAttendance.map((t) => (
                        <tr key={t.teacherId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-800">{t.name}</div>
                            <div className="text-xs text-gray-500">{t.employeeId}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${t.present ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                              {t.present ? "Present" : "Absent"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {t.selfieAttendanceAt ? new Date(t.selfieAttendanceAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{ATTENDANCE_METHOD_LABELS[t.attendanceMethod] || t.attendanceMethod || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {!t.teacherLoginId ? (
                              <span className="text-xs text-gray-400 italic">Not applicable</span>
                            ) : t.principalVerified ? (
                              <button
                                onClick={() => toggleTeacherVerification(t.teacherLoginId, false)}
                                disabled={verifyingId === t.teacherLoginId}
                                className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50"
                                title="Click to revoke verification"
                              >
                                ✓ Verified
                              </button>
                            ) : (
                              <button
                                onClick={() => toggleTeacherVerification(t.teacherLoginId, true)}
                                disabled={verifyingId === t.teacherLoginId}
                                className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 hover:bg-indigo-100 hover:text-indigo-800 disabled:opacity-50"
                              >
                                Verify
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrincipalAttendance;
